import { QueryClient } from '@tanstack/react-query';

import { ComponentMetadataModel, EntityMetadataModel, EntityPropertyMetadataModel, ExpressionMetadataModel, ParameterMetadata, ParameterType, PrimitiveType } from '../../../api/api';
import { ExpressionModel } from '../../FlowDesigner/Models/ExpressionModel';
import { FlowQueries } from '../../FlowDesigner/Hooks/useFlowQueries';
import { VariableScope } from '../../FlowDesigner/Models/VariableScopes/VariableScope';
import { VariableScopeCalculatorServiceBase } from '../../FlowDesigner/Services/VariableScopeCalculatorServiceBase';
import { getEntityMetadataModelFromJsonSchema } from '../../FlowDesigner/Services/EntityMetadataModelConverter';
import { ViewRefModel } from '../../FlowDesigner/Models/ViewRefModel';
import { Bundle } from '../Models/Bundle';
import { IComponentModel } from '../Models/IComponentModel';
import { IContainerComponentModel } from '../Models/IContainerComponentModel';
import { ParameterTypes } from '@@common';
import { SharedComponentModel } from '../Models/SharedComponentModel';
import { ViewModel } from '../Models/ViewModel';
import { ComponentPath } from '../Utils/ComponentTreeUtils';
import { VariableNames } from '../Constants/VariableNames';

/**
 * Parses Express-style route path parameters (e.g. `:userId`) from a route path string.
 * Returns the list of parameter names without the leading colon.
 */
function parseRouteParams(routePath: string | undefined): string[] {
    if (!routePath) return [];
    const segments = routePath.split('/');
    return segments
        .filter((segment) => segment.startsWith(':'))
        .map((segment) => segment.slice(1));
}

/**
 * Computes the variable scope visible to components in a view.
 *
 * The scope is built by traversing the component tree in DFS pre-order and
 * accumulating variableSets from every component that appears before the
 * selected component.  Variables contributed by the selected component itself
 * are NOT included (those become visible to subsequent siblings / descendants).
 *
 * Root scope for Views contains:
 *   - `$view`   — empty object that represents the view's state
 *   - `$input`  — object whose properties are:
 *                 1. Route-path parameters (e.g. `/users/:userId` → `$input.userId: string`)
 *                 2. InputSchemaValue properties (if View.InputSchemaValue is set)
 *                 Route params take precedence over schema properties with the same name
 *   - `$output` — object with properties from View.OutputSchemaValue (if set)
 *
 * Root scope for SharedComponents contains:
 *   - `$view`   — empty object for shared component state
 *   - `$input`  — object with properties from SharedComponent.InputSchemaValue (if set)
 *   - `$output` — object with properties from SharedComponent.OutputSchemaValue (if set)
 */
export class ViewVariableScopeCalculatorService extends VariableScopeCalculatorServiceBase {
    private componentByName: Map<string, ComponentMetadataModel>;
    private expressionMetadataList: ExpressionMetadataModel[];
    private bundle: Bundle | null;

    constructor(
        componentByName: Map<string, ComponentMetadataModel>,
        flowQueries: FlowQueries,
        queryClient: QueryClient,
        expressionMetadataList: ExpressionMetadataModel[] = [],
        bundle: Bundle | null = null,
    ) {
        super(flowQueries, queryClient);
        this.componentByName = componentByName;
        this.expressionMetadataList = expressionMetadataList;
        this.bundle = bundle;
    }

    protected override getExpressionMetadata(expressionType: string): ExpressionMetadataModel | undefined {
        return this.expressionMetadataList.find((m) => m.name === expressionType);
    }

    /**
     * Overrides base implementation to add support for View.InputSchemaValue and View.OutputSchemaValue typeRefs.
     *
     * For ViewRef parameters with suffixes like:
     * - "View.InputSchemaValue" → resolves to the InputSchemaValue schema of the referenced View
     * - "View.OutputSchemaValue" → resolves to the OutputSchemaValue schema of the referenced View
     */
    public override async resolveExpectedObjectType(
        parameterMetadata: ParameterMetadata,
        parameters: ParameterMetadata[],
        data: Record<string, any>,
    ): Promise<EntityMetadataModel | undefined> {
        // First try base implementation (handles OperationRef, FlowRef, SchemaRef, etc.)
        const baseResult = await super.resolveExpectedObjectType(parameterMetadata, parameters, data);
        if (baseResult) {
            return baseResult;
        }

        if (!this.bundle || !parameterMetadata.expectedType?.typeRef) {
            return undefined;
        }

        // Parse suffix notation to extract parameter name and schema selection
        const parsed = this.parseTypeRefSuffix(parameterMetadata.expectedType.typeRef);

        // If no View-specific suffix, not our responsibility
        if (!parsed.useInputSchema && !parsed.useOutputSchema) {
            return undefined;
        }

        const referencedParam = parameters.find((p) => p.name === parsed.paramName);
        if (!referencedParam) {
            return undefined;
        }

        const refValue = data[parsed.paramName];
        if (!refValue) {
            return undefined;
        }

        try {
            // Check if this is a ViewRef (typeCustom === 'ui:ViewId')
            if (referencedParam.typeCustom === ParameterTypes.ViewId) {
                const viewRef = refValue as ViewRefModel;
                if (!viewRef?.Id) {
                    return undefined;
                }

                // Find the view in the bundle
                const view = this.bundle.Views.find((v) => v.Id === viewRef.Id);
                if (!view) {
                    return undefined;
                }

                // Return the appropriate schema
                if (parsed.useInputSchema && view.InputSchemaValue?.Value) {
                    return getEntityMetadataModelFromJsonSchema(view.InputSchemaValue.Value);
                } else if (parsed.useOutputSchema && view.OutputSchemaValue?.Value) {
                    return getEntityMetadataModelFromJsonSchema(view.OutputSchemaValue.Value);
                }
            }
        } catch (error) {
            console.error(`Error resolving View schema for parameter '${parameterMetadata.name}':`, error);
        }

        return undefined;
    }

    /**
     * Returns the variable scope that should be visible when editing the component
     * at `selectedPath` within the given view.
     *
     * When `selectedPath` is null or empty the base scope (`$view` + `$input`) is returned.
     */
    async getVariableScopeForView(view: ViewModel, selectedPath: ComponentPath | null): Promise<VariableScope> {
        const baseScope = this.buildBaseScope(view);

        if (!selectedPath || selectedPath.length === 0) {
            return baseScope;
        }

        const scope = new VariableScope();
        scope.addPrevious(baseScope);

        // Process the view's own expression parameters (e.g. OnOpen) before traversing
        // children so that all components in the view can see the resulting variables.
        await this.processComponentVariableSets(view, scope);
        await this.processComponentExpressionParameterVariableSets(view, scope);

        await this.accumulateScopeUntil(view.Children, [], selectedPath, scope);

        return scope;
    }

    /**
     * Returns the variable scope that should be visible when editing the component
     * at `selectedPath` within a SharedComponent.
     *
     * SharedComponents have a simplified scope with just `$view` (no `$input` route params).
     * The selectedPath should include the root component ID.
     */
    async getVariableScopeForSharedComponent(sharedComponent: SharedComponentModel, selectedPath: ComponentPath | null): Promise<VariableScope> {
        const baseScope = this.buildSharedComponentBaseScope(sharedComponent);

        if (!selectedPath || selectedPath.length === 0) {
            return baseScope;
        }

        const scope = new VariableScope();
        scope.addPrevious(baseScope);

        // SharedComponent.Component is the root container
        // The path includes this root component ID, so we need to handle it specially
        // If the path starts with the root component ID, we process from its children
        const rootComponent = sharedComponent.Component;

        if (selectedPath.length === 1 && selectedPath[0] === rootComponent.Id) {
            // Selecting the root component itself
            return baseScope;
        }

        if (selectedPath[0] === rootComponent.Id) {
            // Process the root component's variableSets and container parameters
            await this.processComponentVariableSets(rootComponent, scope);
            await this.processComponentExpressionParameterVariableSets(rootComponent, scope);
            await this.processContainerParameterVariableSets(rootComponent, scope);

            // Then traverse children with the remaining path
            const childrenPath = selectedPath.slice(1);
            if (rootComponent.Children && childrenPath.length > 0) {
                await this.accumulateScopeUntil(rootComponent.Children, [rootComponent.Id], selectedPath, scope);
            }
        }

        return scope;
    }

    // ---------------------------------------------------------------------------
    // Private helpers
    // ---------------------------------------------------------------------------

    /**
     * Builds the root scope for a SharedComponent:
     *   - `$view` — empty object variable
     *   - `$input` — object whose properties come from the SharedComponent's InputSchemaValue (if set)
     *   - `$output` — object whose properties come from the SharedComponent's OutputSchemaValue (if set)
     */
    private buildSharedComponentBaseScope(sharedComponent: SharedComponentModel): VariableScope {
        const scope = new VariableScope();

        // $view — empty object for shared component state
        const viewEntity: EntityMetadataModel = {
            id: '_view',
            contextId: '',
            name: VariableNames.View,
            description: 'Shared component state variables',
            properties: [],
        };
        scope.setVariable(VariableNames.View, this.getVariableTypeAsSchema(VariableNames.View, viewEntity));

        // $input — object whose properties come from the SharedComponent's InputSchemaValue
        if (sharedComponent.InputSchemaValue?.Value) {
            const inputEntity = getEntityMetadataModelFromJsonSchema(sharedComponent.InputSchemaValue.Value);
            if (inputEntity) {
                scope.setVariable(VariableNames.Input, this.getVariableTypeAsSchema(VariableNames.Input, inputEntity));
            }
        }

        // $output — object whose properties come from the SharedComponent's OutputSchemaValue
        if (sharedComponent.OutputSchemaValue?.Value) {
            const outputEntity = getEntityMetadataModelFromJsonSchema(sharedComponent.OutputSchemaValue.Value);
            if (outputEntity) {
                scope.setVariable(VariableNames.Output, this.getVariableTypeAsSchema(VariableNames.Output, outputEntity));
            }
        }

        return scope;
    }

    /**
     * Builds the root scope for a view:
     *   - `$view`  — empty object variable
     *   - `$input` — object with properties from route-path parameters (as strings) merged with InputSchemaValue properties
     *   - `$output` — object with properties from OutputSchemaValue (if set)
     */
    private buildBaseScope(view: ViewModel): VariableScope {
        const scope = new VariableScope();

        // $view — empty object at the start of the view lifecycle
        const viewEntity: EntityMetadataModel = {
            id: '_view',
            contextId: '',
            name: VariableNames.View,
            description: 'View state variables',
            properties: [],
        };
        scope.setVariable(VariableNames.View, this.getVariableTypeAsSchema(VariableNames.View, viewEntity));

        // $input — merge route-path parameters (as strings) with InputSchemaValue properties
        const routeParams = parseRouteParams(view.RoutePath);
        const inputProperties: EntityPropertyMetadataModel[] = [];

        // Add route parameters as string properties
        if (routeParams.length > 0) {
            inputProperties.push(...routeParams.map((param) => ({
                name: param,
                isRequired: false,
                isMultiple: false,
                isComplex: false,
                type: PrimitiveType.String,
            })));
        }

        // Merge with InputSchemaValue properties if present
        if (view.InputSchemaValue?.Value) {
            const inputSchemaEntity = getEntityMetadataModelFromJsonSchema(view.InputSchemaValue.Value);
            if (inputSchemaEntity?.properties) {
                // Add schema properties, but route params take precedence (don't override)
                const routeParamNames = new Set(routeParams);
                for (const schemaProp of inputSchemaEntity.properties) {
                    if (!routeParamNames.has(schemaProp.name)) {
                        inputProperties.push(schemaProp);
                    }
                }
            }
        }

        // Only add $input if there are any properties (from route or schema)
        if (inputProperties.length > 0) {
            const inputEntity: EntityMetadataModel = {
                id: '_input',
                contextId: '',
                name: VariableNames.Input,
                description: 'View input parameters (route params + InputSchemaValue)',
                properties: inputProperties,
            };
            scope.setVariable(VariableNames.Input, this.getVariableTypeAsSchema(VariableNames.Input, inputEntity));
        }

        // $output — from OutputSchemaValue if present
        if (view.OutputSchemaValue?.Value) {
            const outputEntity = getEntityMetadataModelFromJsonSchema(view.OutputSchemaValue.Value);
            if (outputEntity) {
                scope.setVariable(VariableNames.Output, this.getVariableTypeAsSchema(VariableNames.Output, outputEntity));
            }
        }

        return scope;
    }

    /**
     * DFS pre-order traversal.  For every component encountered:
     *   - If its absolute path equals `targetPath`, return true (stop).
     *   - Otherwise, process its `variableSets` into `scope` and recurse into children.
     *
     * @param children   The children array being iterated at the current depth.
     * @param currentPath The IDs of all ancestors above `children` (built incrementally).
     * @param targetPath  The absolute path (from view root) of the component we stop at.
     * @param scope       Accumulated variable scope that is mutated in-place.
     * @returns true if the target was found (callers should stop further traversal).
     */
    private async accumulateScopeUntil(
        children: IComponentModel[],
        currentPath: string[],
        targetPath: ComponentPath,
        scope: VariableScope,
    ): Promise<boolean> {
        for (const child of children) {
            const childPath = [...currentPath, child.Id];

            // Check whether this node is the target — stop before including it
            if (pathsEqual(childPath, targetPath)) {
                return true;
            }

            // Accumulate this component's variable contributions
            await this.processComponentVariableSets(child, scope);

            // Also process variableSets from expression-type parameters
            // (e.g. OnOpen: DomainService.OperationCall with ResponseVariable → $view.notes)
            await this.processComponentExpressionParameterVariableSets(child, scope);

            // Recurse into children (containers only)
            const container = child as IContainerComponentModel;
            if (Array.isArray(container.Children) && container.Children.length > 0) {
                // Process container parameter variableSets before descending into children
                // (e.g. RepeatContainer's Children.expectedType.variableSets declares $item)
                await this.processContainerParameterVariableSets(child, scope);

                const found = await this.accumulateScopeUntil(container.Children, childPath, targetPath, scope);
                if (found) return true;
            }
        }

        return false;
    }

    /**
     * Processes a component's `variableSets` into the given scope.
     * Looks up the component's metadata by its `Type` string.
     * Silently skips if no metadata or no variableSets are present.
     *
     * Uses a two-phase approach to correctly handle multi-segment nameRef paths
     * (e.g. an InputField whose Value is ["$view", "noteText"] should add `noteText`
     * as a property of the existing $view object rather than overwriting $view).
     */
    private async processComponentVariableSets(component: IComponentModel, scope: VariableScope): Promise<void> {
        const metadata = this.componentByName.get(component.Type);
        if (!metadata?.variableSets || metadata.variableSets.length === 0) {
            return;
        }

        // Phase 1: collect raw contributions into a temp scope
        const tempScope = new VariableScope();
        await this.processVariableSets(
            metadata.variableSets,
            metadata.parameters,
            component as Record<string, any>,
            scope,     // input scope for type resolution
            tempScope, // output scope — raw contributions before path merging
            `component '${component.Type}'`,
        );

        // Phase 2: merge multi-segment path contributions (e.g. ["$view","noteText"])
        // into the existing root variable entity instead of overwriting it.
        this.mergeMultiSegmentPathContributions(
            metadata.variableSets,
            component as Record<string, any>,
            tempScope,
            scope, // context scope — read existing root variable entities
            scope, // output scope — write merged result
        );
    }

    /**
     * Processes expression-level variableSets contributed by Expression-type parameters on a component.
     *
     * Some components have Expression-type parameters (e.g. OnOpen, OnChange) whose value is an
     * expression such as DomainService.OperationCall. When that expression declares variableSets
     * (e.g. storing its response at ResponseVariable = ["$view", "notes"]), subsequent siblings
     * and their descendants should see the resulting variable in scope.
     *
     * Only top-level expression variableSets are processed — parameter-level variableSets and
     * nested sub-expressions (inside And/Or collections) are not promoted to the view scope.
     */
    private async processComponentExpressionParameterVariableSets(component: IComponentModel, scope: VariableScope): Promise<void> {
        const metadata = this.componentByName.get(component.Type);
        if (!metadata?.parameters) return;

        const expressionParams = metadata.parameters.filter((p) => p.type === ParameterType.Expression);
        for (const param of expressionParams) {
            const value = (component as Record<string, any>)[param.name];
            if (!value || typeof value !== 'object' || !value.Type) continue;

            await this.processExpressionVariableSetsInto(value as ExpressionModel, scope, scope);
        }
    }

    /**
     * Processes variableSets from Container-type parameters into the given scope.
     * This makes variables like `$item` available to child components inside a container.
     *
     * Example: RepeatContainer's Children parameter has expectedType.variableSets that
     * declares `$item` (unwrapped from the Items expression) — children placed inside
     * the container should see `$item` in their scope.
     */
    private async processContainerParameterVariableSets(component: IComponentModel, scope: VariableScope): Promise<void> {
        const metadata = this.componentByName.get(component.Type);
        if (!metadata?.parameters) {
            return;
        }

        // Find Container-type parameters
        const containerParams = metadata.parameters.filter((p) => p.type === 'Container');

        for (const param of containerParams) {
            if (param.expectedType?.variableSets && param.expectedType.variableSets.length > 0) {
                await this.processVariableSets(
                    param.expectedType.variableSets,
                    metadata.parameters,
                    component as Record<string, any>,
                    scope,
                    scope,
                    `container parameter '${param.name}' of component '${component.Type}'`,
                );
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function pathsEqual(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, i) => id === b[i]);
}
